import { Project } from '@awork/features/project/models/project.model'
import { TimeTracking } from '@awork/features/time-tracking/models/time-tracking.model'
import { TaskStatus } from '@awork/features/task/models/task-status.model'
import { ProjectStatus } from '@awork/features/project/models/project-status.model'
import { User } from '@awork/features/user/models/user.model'
import { Task } from '@awork/features/task/models/task.model'
import type { Reaction } from '@awork/_shared/models/comment.model'
import { Comment } from '@awork/_shared/models/comment.model'
import replace from '@awork/_shared/functions/replace'
import { stripHTML } from '@awork/_shared/functions/string-operations'
import { differenceInCalendarDays } from '@awork/_shared/functions/date-fns-wrappers'
import { DatePipe } from '@angular/common'
import getLanguage from '@awork/_shared/functions/get-language'
import { unescape } from '@awork/_shared/functions/lodash'
import { Color } from '@awork/_shared/types/color'
import type {
  ConversationType,
  EventNotificationType,
  GroupingType,
  NotificationGroup,
  NotificationType,
  ConversationsGroup,
  ProjectGroupType,
  ValidEntityTypes,
  GroupNameByParentEntity
} from '@awork/framework/models/notification.types'
import {
  CONVERSATION_TYPES,
  EVENT_NOTIFICATION_TYPES,
  NotificationEvents
} from '@awork/framework/models/notification.types'
import { Document } from '@awork/features/document/models/document.model'
import { DocumentSpace } from '@awork/features/document/models/document-space.model'
import { FilterEntity } from '@awork/_shared/models/filters.model'
import { fileTagRegex } from '@awork/_shared/static-data/regular-expressions'
import {
  MY_PROFILE_AI_SECTION,
  MY_PROFILE_SECTION_QUERY_PARAM
} from '@awork/features/user/models/my-profile-section.model'

/** Mobile app destination of a notification. */
export type MobileNotificationTarget =
  | { kind: 'task' | 'project' | 'document'; id: string; commentId?: string }
  | { kind: 'documentSpace' | 'thread'; id: string }
  | { kind: 'export'; file: NonNullable<INotification['file']> }
  | { kind: 'timer' | 'inbox' | 'assistant' | 'taskList' | 'projectList' }

export interface INotification {
  id: string
  workspaceId: string
  isNewConversation?: boolean
  userId: string
  type: NotificationType
  event: NotificationEvents
  seen: boolean
  clicked: boolean
  seenOn?: Date | string
  projectId?: string
  project?: Project
  taskId?: string
  task?: Task
  timeTrackingId?: string
  timeTracking?: TimeTracking
  documentId?: string
  document?: Document
  documentSpaceId?: string
  documentSpace?: DocumentSpace
  filterId?: string
  filter?: FilterEntity
  commentId?: string
  comment?: Comment
  reaction?: Reaction
  taskStatusId?: string
  taskStatus?: TaskStatus
  projectStatusId?: string
  projectStatus?: ProjectStatus
  responsibleUserId?: string
  responsibleUser?: User
  userInNotificationId?: string
  userInNotification?: User
  createdOn: Date | string
  visible?: boolean
  notifications?: Notification[]
  groupingType?: GroupingType
  reminderOfNotificationId?: string
  file?: {
    name: string
    entityName: string
    url: string
  }
  currentUser?: User | null
  expanded?: boolean

  isScheduledTaskSyncSuccessful?: boolean // Only for TaskScheduleSync notifications
  automationFailureCode?: string
  automationFailureDescription?: string
  automationId?: string
  agentThreadId?: string
  agentThreadTitle?: string
  agentId?: string
  agentName?: string
}

type NotificationNavigationCommand = string | { outlets: { agentModal: string[] } }

export class Notification implements INotification {
  id: string
  workspaceId: string
  userId: string
  type: NotificationType
  event: NotificationEvents
  isNewConversation: boolean
  seen: boolean
  seenOn?: Date | string
  clicked: boolean
  projectId?: string
  project?: Project
  taskId?: string
  task?: Task
  timeTrackingId?: string
  timeTracking?: TimeTracking
  documentId?: string
  document?: Document
  documentSpaceId?: string
  documentSpace?: DocumentSpace
  filterId?: string
  filter?: FilterEntity
  commentId?: string
  comment?: Comment
  reaction?: Reaction
  taskStatusId?: string
  taskStatus?: TaskStatus
  projectStatusId?: string
  projectStatus?: ProjectStatus
  responsibleUserId?: string
  responsibleUser?: User
  userInNotificationId?: string
  userInNotification?: User
  createdOn: Date | string
  visible?: boolean
  notifications?: Notification[]
  groupingType?: GroupingType
  reminderOfNotificationId?: string
  file?: {
    name: string
    entityName: string
    url: string
  }
  currentUser?: User | null
  expanded?: boolean
  uiProperties?: {
    hide: boolean
  }

  isScheduledTaskSyncSuccessful?: boolean // Only for TaskScheduleSync notifications
  automationFailureCode?: string
  automationFailureDescription?: string
  automationId?: string
  agentThreadId?: string
  agentThreadTitle?: string
  agentId?: string
  agentName?: string

  translations = q.translations.Notifications

  constructor(notification: INotification, currentUser?: User) {
    Object.assign(this, notification)

    // Older persisted notifications can contain an outdated translation snapshot.
    Object.defineProperty(this, 'translations', {
      value: q.translations.Notifications,
      enumerable: false
    })

    if (currentUser) {
      this.currentUser = currentUser
    }

    this.mapNotification()
    this.setUIProperties()
  }

  /**
   * Get the ungrouped notifications
   * @param {NotificationGroup[]} notificationGroups
   * @returns {Notification[]}
   */
  static getNotifications(notificationGroups: NotificationGroup[]): Notification[] {
    return notificationGroups.reduce((allNotifications, groupItem) => {
      if (groupItem.groupType === 'default') {
        return groupItem.groups as Notification[]
      }

      return (groupItem.groups as ConversationsGroup[]).reduce((allConversations, projectConversations) => {
        return allConversations.concat(projectConversations.notifications)
      }, [] as Notification[])
    }, [] as Notification[])
  }

  /**
   * Maps the properties of the notification
   */
  mapNotification(): void {
    if (this.seenOn) {
      this.seenOn = new Date(this.seenOn)
    }

    if (this.createdOn) {
      this.createdOn = new Date(this.createdOn)
    }

    if (this.project) {
      this.project = Project.mapProject(this.project)

      if (this.task && !this.task.project) {
        this.task.project = this.project
      }
    } else if (this.task?.project) {
      this.project = Project.mapProject(this.task.project)
    }

    if (this.task) {
      this.task = Task.mapTask(this.task)
    }

    if (this.timeTracking) {
      this.timeTracking = new TimeTracking(this.timeTracking)
    }

    if (this.comment) {
      this.comment = new Comment(this.comment)
    }

    if (this.taskStatus) {
      this.taskStatus = new TaskStatus(this.taskStatus)
    }

    if (this.projectStatus) {
      this.projectStatus = new ProjectStatus(this.projectStatus)
    }

    if (this.responsibleUser) {
      this.responsibleUser = new User(this.responsibleUser, 'Notification')
    }

    if (this.userInNotification) {
      this.userInNotification = new User(this.userInNotification, 'Notification')
    }

    if (this.document) {
      this.document = new Document(this.document)
    }

    if (this.documentSpace) {
      this.documentSpace = new DocumentSpace(this.documentSpace)
    }

    if (this.filter) {
      this.filter = new FilterEntity(this.filter)
    }

    // For grouping purposes
    this.notifications = []
  }

  /**
   * Sets the properties used in the UI
   * @private
   */
  setUIProperties(): void {
    this.uiProperties = {
      hide: false
    }
  }

  /**
   * Gets the icon of the notification based on the event
   * @returns {string}
   */
  get icon(): string {
    switch (this.event) {
      case NotificationEvents.TaskCommented:
      case NotificationEvents.TaskCommentReacted:
      case NotificationEvents.ProjectCommented:
      case NotificationEvents.TaskMentioned:
      case NotificationEvents.ProjectMentioned:
      case NotificationEvents.DocumentCommented:
      case NotificationEvents.DocumentMentioned:
      case NotificationEvents.DocumentCommentMentioned:
      case NotificationEvents.DocumentCommentReacted:
        return 'chat'
      case NotificationEvents.TaskAssigned:
      case NotificationEvents.TaskUnassigned:
      case NotificationEvents.ProjectAssigned:
      case NotificationEvents.ProjectAutoConnected:
      case NotificationEvents.DocumentContributorAdded:
      case NotificationEvents.DocumentSpaceContributorAdded:
      case NotificationEvents.FilterContributorAdded:
        return 'person'
      case NotificationEvents.TaskStatusChanged:
      case NotificationEvents.ProjectStatusChanged:
        return 'arrow_forward'
      case NotificationEvents.TaskDue:
      case NotificationEvents.ProjectDue:
      case NotificationEvents.ProjectDueWeekend:
      case NotificationEvents.MonthlyTimeEntriesReport:
      case NotificationEvents.WeeklyTimeEntriesReport:
        return 'alarm'
      case NotificationEvents.Birthday:
        return 'cake'
      case NotificationEvents.ExportAvailable:
        return 'file_download'
      case NotificationEvents.ExportFailed:
        return 'error_outline'
      case NotificationEvents.TaskScheduleSyncCompleted:
        return this.isScheduledTaskSyncSuccessful ? 'event' : 'error_outline'
      case NotificationEvents.BillingInvoiceTransferSucceeded:
        return 'receipt_long'
      case NotificationEvents.MeetingRecordingBlocked:
      case NotificationEvents.BillingInvoiceTransferNeedsAttention:
        return 'error_outline'
      case NotificationEvents.PlannerAutofixComplete:
      case NotificationEvents.PlannerRefineComplete:
      case NotificationEvents.AgentThreadShared:
      case NotificationEvents.AgentMeetingSummarized:
      case NotificationEvents.AgentThreadAwaitingAction:
      case NotificationEvents.AgentShared:
      case NotificationEvents.AiCreditsLow:
      case NotificationEvents.AiCreditsUsedUp:
      case NotificationEvents.AiPlanUpgraded:
        return 'ai'
      case NotificationEvents.AutomationDeactivated:
      case NotificationEvents.AiAgentRunBlocked:
      case NotificationEvents.AiAgentRunBlockedAdmin:
        return 'error_outline'
      default:
        return 'notifications'
    }
  }

  /**
   * Gets the notification description with the responsible user's name when available.
   * @returns {string} The translated notification description.
   */
  get description(): string {
    let userName: string

    if (this.responsibleUser) {
      userName = this.responsibleUser.shortName
    }

    switch (this.event) {
      case NotificationEvents.TaskAssigned:
        return replace(this.translations.taskAssigned.description, { userName })
      case NotificationEvents.TaskUnassigned:
        return replace(this.translations.taskUnassigned.description, { userName })
      case NotificationEvents.TaskStatusChanged:
        return replace(this.translations.taskStatusChanged.description, { userName })
      case NotificationEvents.TaskCommented:
        if (this.task) {
          return replace(this.translations.entityCommented.description, {
            userName,
            entityName: this.truncatedEntityName
          })
        }

        break
      case NotificationEvents.TaskMentioned:
        if (this.task) {
          return replace(this.translations.entityMentioned.description, {
            userName,
            entityName: this.truncatedEntityName
          })
        }

        break
      case NotificationEvents.ProjectAssigned:
        return replace(this.translations.projectAssigned.description, { userName })
      case NotificationEvents.ProjectAutoConnected:
        return replace(this.translations.projectAutoConnected.description, { userName })
      case NotificationEvents.ProjectStatusChanged:
        return replace(this.translations.projectStatusChanged.description, { userName })
      case NotificationEvents.ProjectCommented:
        if (this.project) {
          return replace(this.translations.entityCommented.description, {
            userName,
            entityName: this.truncatedEntityName
          })
        }

        break
      case NotificationEvents.ProjectMentioned:
        if (this.project) {
          return replace(this.translations.entityMentioned.description, {
            userName,
            entityName: this.truncatedEntityName
          })
        }

        break
      case NotificationEvents.DocumentCommented:
        if (this.document) {
          return replace(this.translations.entityCommented.description, {
            userName,
            entityName: this.truncatedEntityName
          })
        }

        break
      case NotificationEvents.DocumentMentioned:
        if (this.document) {
          return replace(this.translations.entityMentioned.description, {
            userName,
            entityName: this.truncatedEntityName
          })
        }

        break
      case NotificationEvents.DocumentCommentMentioned:
        if (this.document) {
          return replace(this.translations.entityCommentMentioned.description, {
            userName,
            entityName: this.truncatedEntityName
          })
        }

        break
      case NotificationEvents.ProjectDue:
      case NotificationEvents.ProjectDueWeekend:
        if (this.project) {
          return replace(this.translations.projectDue.description, {
            projectName: this.truncatedEntityName
          })
        }

        break
      case NotificationEvents.TaskDue:
        if (this.task) {
          return replace(this.translations.taskDue.description, {
            taskName: this.truncatedEntityName
          })
        }

        break
      case NotificationEvents.WeeklyTimeEntriesReport:
        return this.translations.missingTimeEntriesWeek.description
      case NotificationEvents.MonthlyTimeEntriesReport:
        return this.translations.missingTimeEntriesMonth.description
      case NotificationEvents.Birthday:
        if (this.userInNotification) {
          return this.userInNotificationId === this.currentUser?.id
            ? replace(this.translations.currentUserBirthday.description, {
                emoji: this.getBirthdayEmoji()
              })
            : replace(this.translations.birthday.description, {
                name: this.userInNotification.shortName,
                emoji: this.getBirthdayEmoji()
              })
        }

        break
      case NotificationEvents.ExportAvailable:
        return this.translations.exportAvailable.description
      case NotificationEvents.ExportFailed:
        return this.translations.exportFailed.description
      case NotificationEvents.TaskScheduleSyncCompleted: {
        const { successDescription, failureDescription } = this.translations.taskScheduleSync

        return this.isScheduledTaskSyncSuccessful ? successDescription : failureDescription
      }
      case NotificationEvents.BillingInvoiceTransferSucceeded:
        return this.translations.billingInvoiceTransfer.succeeded
      case NotificationEvents.BillingInvoiceTransferNeedsAttention:
        return this.translations.billingInvoiceTransfer.needsAttention
      case NotificationEvents.CommentReacted:
      case NotificationEvents.TaskCommentReacted:
      case NotificationEvents.ProjectCommentReacted:
      case NotificationEvents.DocumentCommentReacted: {
        const isOwnComment = this.comment?.createdBy === this.currentUser?.id
        const translation = isOwnComment
          ? this.translations.myCommentReacted.description
          : this.translations.commentReacted.description

        return replace(translation, {
          userName,
          reaction: this.reaction?.emoji
        })
      }
      case NotificationEvents.PlannerAutofixComplete:
      case NotificationEvents.PlannerRefineComplete:
        return this.translations.plannerAiCompleted.description
      case NotificationEvents.DocumentContributorAdded:
        return replace(this.translations.documentContributorAdded.description, { userName })
      case NotificationEvents.DocumentSpaceContributorAdded:
        return replace(this.translations.documentSpaceContributorAdded.description, { userName })
      case NotificationEvents.FilterContributorAdded:
        return replace(this.translations.filterContributorAdded.description, { userName })
      case NotificationEvents.AutomationDeactivated:
        return this.getAutomationDeactivatedDescription()
      case NotificationEvents.AgentThreadShared:
        return this.getAgentThreadSharedDescription(userName)
      case NotificationEvents.MeetingRecordingBlocked:
        return this.translations.meetingRecordingBlocked.description
      case NotificationEvents.AgentMeetingSummarized:
        return this.translations.agentMeetingSummarized.description
      case NotificationEvents.AgentThreadAwaitingAction:
        return this.translations.agentThreadAwaitingAction.description
      case NotificationEvents.AgentShared:
        return replace(this.translations.agentShared.description, { userName })
      case NotificationEvents.AiCreditsLow:
      case NotificationEvents.AiCreditsUsedUp:
      case NotificationEvents.AiAgentRunBlocked:
      case NotificationEvents.AiAgentRunBlockedAdmin:
      case NotificationEvents.AiPlanUpgraded:
        return this.getAiCreditDescription()
    }

    return null
  }

  /**
   * Gets the description of an AI credit notification.
   * The user in the notification is the user whose AI credits are concerned.
   * Admins see that user's name when the blocked agent belongs to someone else.
   * @returns {string} The description
   */
  private getAiCreditDescription(): string {
    const translations = this.translations.aiCredits
    const userName = this.userInNotification?.shortName
    const agentName = this.agentName

    switch (this.event) {
      case NotificationEvents.AiCreditsLow:
        return replace(translations.lowDescription, { userName })
      case NotificationEvents.AiCreditsUsedUp:
        return replace(translations.usedUpDescription, { userName })
      case NotificationEvents.AiAgentRunBlockedAdmin:
        if (userName) {
          return replace(translations.userAgentRunBlockedDescription, { userName, agentName })
        }

        return replace(translations.agentRunBlockedDescription, { agentName })
      case NotificationEvents.AiAgentRunBlocked:
        return replace(translations.agentRunBlockedDescription, { agentName })
      case NotificationEvents.AiPlanUpgraded:
        return translations.planUpgradedDescription
      default:
        return null
    }
  }

  /**
   * Names the thread sender or uses a generic share title when the sender is unavailable.
   * @param {string | undefined} userName - The sender's display name, when available.
   * @returns {string} The translated share notification description.
   */
  private getAgentThreadSharedDescription(userName: string | undefined): string {
    if (!userName) {
      return this.translations.agentThreadShared.descriptionWithoutUser
    }

    return replace(this.translations.agentThreadShared.description, { userName })
  }

  /**
   * Gets the description of the group notification based on the grouping type and event
   * @returns {string}
   */
  get groupDescription(): string {
    if (this.groupingType && this.notifications && this.notifications.length > 0) {
      let userName: string

      if (this.responsibleUser) {
        userName = this.responsibleUser.shortName
      }

      switch (this.groupingType) {
        case 'event-entity-user':
          return this.getEventEntityUserDescription(userName, this.truncatedEntityName)
        case 'event-entity':
          return this.getEventEntityDescription(this.truncatedEntityName)
        case 'event-user':
          return this.getEventUserDescription(userName)
        case 'event-reminder':
          return this.getEventReminderDescription()
      }
    }

    return null
  }

  /**
   * Gets the description of the group notification based on the event, entity and the user
   * @param {string} userName The name of the user
   * @param {string} entityName The name of the entity
   * @returns {string} The description
   */
  private getEventEntityUserDescription(userName: string, entityName: string): string {
    switch (this.event) {
      case NotificationEvents.TaskAssigned:
        return replace(this.translations.eventEntityUser.taskAssigned, {
          userName,
          entityName
        })
      case NotificationEvents.TaskUnassigned:
        return replace(this.translations.eventEntityUser.taskUnassigned, {
          userName,
          entityName
        })
      case NotificationEvents.ProjectAssigned:
        return replace(this.translations.eventEntityUser.projectAssigned, {
          userName,
          entityName
        })
      case NotificationEvents.ProjectAutoConnected:
        return replace(this.translations.eventEntityUser.projectAutoConnected, {
          userName,
          entityName
        })
      case NotificationEvents.TaskCommented:
        return replace(this.translations.eventEntityUser.taskCommented, {
          userName,
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.TaskCommentReacted:
        return replace(this.translations.eventEntityUser.taskCommentReacted, {
          userName,
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.ProjectCommentReacted:
        return replace(this.translations.eventEntityUser.projectCommentReacted, {
          userName,
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.TaskMentioned:
        return replace(this.translations.eventEntityUser.taskMentioned, {
          userName,
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.ProjectCommented:
        return replace(this.translations.eventEntityUser.projectCommented, {
          userName,
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.ProjectMentioned:
        return replace(this.translations.eventEntityUser.projectMentioned, {
          userName,
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.TaskStatusChanged:
        return replace(this.translations.eventEntityUser.taskStatusChanged, {
          userName,
          entityName
        })
      case NotificationEvents.ProjectStatusChanged:
        return replace(this.translations.eventEntityUser.projectStatusChanged, {
          userName,
          entityName
        })
      case NotificationEvents.ExportAvailable:
        return replace(this.translations.eventEntityUser.exportAvailable, {
          count: this.notifications.length
        })
      case NotificationEvents.ExportFailed:
        return replace(this.translations.eventEntityUser.exportFailed, {
          count: this.notifications.length
        })
      case NotificationEvents.DocumentCommented:
        return replace(this.translations.eventEntityUser.documentCommented, {
          userName,
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.DocumentCommentMentioned:
        return replace(this.translations.eventEntityUser.documentCommentMentioned, {
          userName,
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.DocumentCommentReacted:
        return replace(this.translations.eventEntityUser.documentCommentReacted, {
          userName,
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.DocumentMentioned:
        return replace(this.translations.eventEntityUser.documentMentioned, {
          userName,
          entityName,
          count: this.notifications.length
        })
    }

    return null
  }

  /**
   * Gets the description of the group notification based on the event and the entity
   * @param {string} entityName The name of the entity
   * @returns {string} The description
   */
  private getEventEntityDescription(entityName: string): string {
    switch (this.event) {
      case NotificationEvents.TaskAssigned:
        return replace(this.translations.eventEntity.taskAssigned, {
          entityName
        })
      case NotificationEvents.TaskUnassigned:
        return replace(this.translations.eventEntity.taskUnassigned, {
          entityName
        })
      case NotificationEvents.ProjectAssigned:
        return replace(this.translations.eventEntity.projectAssigned, {
          entityName
        })
      case NotificationEvents.ProjectAutoConnected:
        return replace(this.translations.eventEntity.projectAutoConnected, {
          entityName
        })
      case NotificationEvents.TaskCommented:
        return replace(this.translations.eventEntity.taskCommented, {
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.TaskMentioned:
        return replace(this.translations.eventEntity.taskMentioned, {
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.ProjectCommented:
        return replace(this.translations.eventEntity.projectCommented, {
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.ProjectMentioned:
        return replace(this.translations.eventEntity.projectMentioned, {
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.TaskStatusChanged:
        return replace(this.translations.eventEntity.taskStatusChanged, {
          entityName
        })
      case NotificationEvents.ProjectStatusChanged:
        return replace(this.translations.eventEntity.projectStatusChanged, {
          entityName
        })
      case NotificationEvents.TaskCommentReacted:
        return replace(this.translations.eventEntity.taskCommentReacted, {
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.ProjectCommentReacted:
        return replace(this.translations.eventEntity.projectCommentReacted, {
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.DocumentCommented:
        return replace(this.translations.eventEntity.documentCommented, {
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.DocumentCommentMentioned:
        return replace(this.translations.eventEntity.documentCommentMentioned, {
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.DocumentCommentReacted:
        return replace(this.translations.eventEntity.documentCommentReacted, {
          entityName,
          count: this.notifications.length
        })
      case NotificationEvents.DocumentMentioned:
        return replace(this.translations.eventEntity.documentMentioned, {
          entityName,
          count: this.notifications.length
        })
    }

    return null
  }

  /**
   * Gets the description of the group notification based on the event and the user
   * @param {string} userName The name of the user
   * @returns {string} The description
   */
  private getEventUserDescription(userName: string): string {
    switch (this.event) {
      case NotificationEvents.TaskAssigned:
        return replace(this.translations.eventUser.taskAssigned, {
          userName,
          count: this.notifications.length
        })
      case NotificationEvents.TaskUnassigned:
        return replace(this.translations.eventUser.taskUnassigned, {
          userName,
          count: this.notifications.length
        })
      case NotificationEvents.ProjectAssigned:
        return replace(this.translations.eventUser.projectAssigned, {
          userName,
          count: this.notifications.length
        })
      case NotificationEvents.ProjectAutoConnected:
        return replace(this.translations.eventUser.projectAutoConnected, {
          userName,
          count: this.notifications.length
        })
      case NotificationEvents.TaskCommented:
        return replace(this.translations.eventUser.taskCommented, {
          userName,
          count: this.notifications.length
        })
      case NotificationEvents.TaskMentioned:
        return replace(this.translations.eventUser.taskMentioned, {
          userName,
          count: this.notifications.length
        })
      case NotificationEvents.ProjectCommented:
        return replace(this.translations.eventUser.projectCommented, {
          userName,
          count: this.notifications.length
        })
      case NotificationEvents.ProjectMentioned:
        return replace(this.translations.eventUser.projectMentioned, {
          userName,
          count: this.notifications.length
        })
      case NotificationEvents.TaskStatusChanged:
        return replace(this.translations.eventUser.taskStatusChanged, {
          userName,
          count: this.notifications.length
        })
      case NotificationEvents.ProjectStatusChanged:
        return replace(this.translations.eventUser.projectStatusChanged, {
          userName,
          count: this.notifications.length
        })
      case NotificationEvents.TaskCommentReacted:
        return replace(this.translations.eventUser.taskCommentReacted, {
          userName,
          count: this.notifications.length
        })
      case NotificationEvents.ProjectCommentReacted:
        return replace(this.translations.eventUser.projectCommentReacted, {
          userName,
          count: this.notifications.length
        })
      case NotificationEvents.DocumentCommented:
        return replace(this.translations.eventUser.documentCommented, {
          userName,
          count: this.notifications.length
        })
      case NotificationEvents.DocumentCommentReacted:
        return replace(this.translations.eventUser.documentCommentReacted, {
          userName,
          count: this.notifications.length
        })
      case NotificationEvents.DocumentCommentMentioned:
        return replace(this.translations.eventUser.documentCommentMentioned, {
          userName,
          count: this.notifications.length
        })
      case NotificationEvents.DocumentMentioned:
        return replace(this.translations.eventUser.documentMentioned, {
          userName,
          count: this.notifications.length
        })
    }

    return null
  }

  /**
   * Describes the reminder for its task or project.
   * @returns {string} The reminder text, or null for other events.
   */
  private getEventReminderDescription(): string {
    switch (this.event) {
      case NotificationEvents.ProjectDue:
        return replace(this.translations.reminder.projectDue, {
          count: this.notifications.length
        })
      case NotificationEvents.ProjectDueWeekend:
        return replace(this.translations.reminder.projectDueWeekend, {
          count: this.notifications.length
        })
      case NotificationEvents.TaskDue:
        return replace(this.translations.reminder.taskDue, {
          count: this.notifications.length
        })
      case NotificationEvents.WeeklyTimeEntriesReport:
        return this.translations.missingTimeEntriesWeek.description
      case NotificationEvents.MonthlyTimeEntriesReport:
        return this.translations.missingTimeEntriesMonth.description
      case NotificationEvents.Birthday:
        return replace(this.translations.reminder.birthday, {
          count: this.notifications.length,
          emoji: this.getBirthdayEmoji()
        })
      default:
        return null
    }
  }

  /**
   * Gets the color of the notification based on the event
   * This color is used for the text and icon color
   * @returns {string}
   */
  get color(): string {
    switch (this.event) {
      case NotificationEvents.TaskAssigned:
      case NotificationEvents.TaskUnassigned:
      case NotificationEvents.ProjectAssigned:
      case NotificationEvents.ProjectAutoConnected:
      case NotificationEvents.DocumentContributorAdded:
      case NotificationEvents.DocumentSpaceContributorAdded:
      case NotificationEvents.FilterContributorAdded:
      case NotificationEvents.AgentShared:
        return 'green'
      case NotificationEvents.TaskStatusChanged:
      case NotificationEvents.ProjectStatusChanged:
        return 'yellow'
      case NotificationEvents.ProjectDue:
      case NotificationEvents.ProjectDueWeekend:
      case NotificationEvents.TaskDue:
      case NotificationEvents.WeeklyDueTasksSummary:
      case NotificationEvents.TimeTrackingReminder:
      case NotificationEvents.MonthlyTimeEntriesReport:
      case NotificationEvents.WeeklyTimeEntriesReport:
      case NotificationEvents.AutomationDeactivated:
      case NotificationEvents.ExportFailed:
        return 'red'
      case NotificationEvents.BillingInvoiceTransferSucceeded:
        return 'green'
      case NotificationEvents.MeetingRecordingBlocked:
      case NotificationEvents.BillingInvoiceTransferNeedsAttention:
        return 'red'
      case NotificationEvents.ExportAvailable:
        return 'steel'
      case NotificationEvents.AgentThreadShared:
      case NotificationEvents.AgentMeetingSummarized:
      case NotificationEvents.AgentThreadAwaitingAction:
        return 'blue'
      case NotificationEvents.AiCreditsLow:
        return 'yellow'
      case NotificationEvents.AiCreditsUsedUp:
      case NotificationEvents.AiAgentRunBlocked:
      case NotificationEvents.AiAgentRunBlockedAdmin:
        return 'red'
      case NotificationEvents.AiPlanUpgraded:
        return 'green'
      case NotificationEvents.TaskCommented:
      case NotificationEvents.ProjectCommented:
      case NotificationEvents.TaskMentioned:
      case NotificationEvents.ProjectMentioned:
      case NotificationEvents.TaskCommentReacted:
      case NotificationEvents.ProjectCommentReacted:
      case NotificationEvents.CommentReacted:
      case NotificationEvents.DocumentCommented:
      case NotificationEvents.DocumentMentioned:
      case NotificationEvents.DocumentCommentMentioned:
      case NotificationEvents.DocumentCommentReacted:
      default:
        return 'blue'
    }
  }

  /**
   * Gets the text of the notification based on the event
   * @returns {string}
   */
  get text(): string {
    switch (this.event) {
      case NotificationEvents.TaskAssigned:
      case NotificationEvents.TaskUnassigned:
      case NotificationEvents.TaskStatusChanged:
      case NotificationEvents.TaskDue:
        if (this.task) {
          return this.truncatedEntityName
        }

        break
      case NotificationEvents.ProjectAssigned:
      case NotificationEvents.ProjectStatusChanged:
      case NotificationEvents.ProjectDueWeekend:
      case NotificationEvents.ProjectAutoConnected:
        if (this.project) {
          return this.truncatedEntityName
        }

        break
      case NotificationEvents.DocumentContributorAdded:
        if (this.document) {
          return this.truncatedEntityName
        }

        break
      case NotificationEvents.DocumentSpaceContributorAdded:
        if (this.documentSpace) {
          return this.truncatedEntityName
        }

        break
      case NotificationEvents.FilterContributorAdded:
        if (this.filter) {
          return this.truncatedEntityName
        }

        break
      case NotificationEvents.AutomationDeactivated:
        if (this.project?.name) {
          return this.truncatedEntityName
        }

        return this.translations.automationDeactivated.projectEntityName
      case NotificationEvents.AgentThreadShared:
      case NotificationEvents.AgentMeetingSummarized:
      case NotificationEvents.AgentThreadAwaitingAction:
        return this.truncateString(this.agentThreadTitle, 40)
      case NotificationEvents.AgentShared:
        return this.truncateString(this.agentName, 40)
      case NotificationEvents.Birthday:
        if (this.groupingType !== 'no-group') {
          return this.getBirthdayGroupText()
        }

        if (this.userInNotificationId === this.currentUser?.id) {
          return this.translations.currentUserBirthday.text
        }

        return replace(this.translations.birthday.text, { name: this.userInNotification.fullName })
    }

    return null
  }

  /**
   * Gets the detail of the notification based on the event
   * @returns {string}
   */
  get detail(): string {
    switch (this.event) {
      case NotificationEvents.TaskStatusChanged:
        if (!this.taskStatus) {
          return ''
        }

        return replace(this.translations.entityStatusChanged.detail, {
          statusColor: this.taskStatus.color === Color.Fog ? Color.Steel : this.taskStatus.color,
          statusName: this.taskStatus.name
        })
      case NotificationEvents.ProjectStatusChanged:
        if (!this.projectStatus) {
          return ''
        }

        return replace(this.translations.entityStatusChanged.detail, {
          statusColor: this.projectStatus.color,
          statusName: this.projectStatus.name
        })
      case NotificationEvents.TaskDue:
        return this.getDueDateDetail(this.task.dueOn)
      case NotificationEvents.ProjectDue:
      case NotificationEvents.ProjectDueWeekend:
        return this.getDueDateDetail(this.project.dueDate)
      case NotificationEvents.TaskCommented:
      case NotificationEvents.TaskCommentReacted:
      case NotificationEvents.ProjectCommented:
      case NotificationEvents.ProjectCommentReacted:
      case NotificationEvents.CommentReacted:
      case NotificationEvents.TaskMentioned:
      case NotificationEvents.ProjectMentioned:
      case NotificationEvents.DocumentCommented:
      case NotificationEvents.DocumentCommentMentioned:
      case NotificationEvents.DocumentCommentReacted:
        if (this.comment) {
          return this.truncatedComment
        }

        break
      case NotificationEvents.ExportAvailable:
        return this.translations.exportAvailable.detail
      case NotificationEvents.ExportFailed:
        return this.translations.exportFailed.detail
      case NotificationEvents.AutomationDeactivated:
        return this.getAutomationDeactivatedDetail()
      case NotificationEvents.AgentThreadAwaitingAction:
        return this.translations.agentThreadAwaitingAction.detail
    }

    return null
  }

  /**
   * Gets the entity due date text for the detail of the notification
   * @param {Date | string} dueDate
   * @returns {string}
   */
  private getDueDateDetail(dueDate: Date | string): string {
    const due = new Date(dueDate)

    due.setHours(0)
    due.setMinutes(0)

    const today = new Date()

    today.setHours(0)
    today.setMinutes(0)

    const days = differenceInCalendarDays(due, today)
    const translation = this.translations.entityDue.detail

    switch (true) {
      case days === 0:
        return translation.today
      case days === 1:
        return translation.tomorrow
      case days > 1:
        return replace(translation.days, { days })
      default:
        return replace(translation.past, {
          dueDate: new DatePipe(getLanguage()).transform(dueDate ? new Date(dueDate) : new Date(), 'mediumDate')
        })
    }
  }

  /**
   * Gets the birthday text grouping the name of the users
   * @returns {string}
   */
  private getBirthdayGroupText(): string {
    const names: string[] = []

    this.notifications.forEach(notification => {
      names.push(notification.userInNotification.shortName)
    })

    // Join the names with ',' and replace the last one with 'and'
    let joinedNames = names.join(', ')

    joinedNames =
      joinedNames.slice(0, joinedNames.lastIndexOf(',')) +
      joinedNames.slice(joinedNames.lastIndexOf(',')).replace(',', ` ${q.translations.common.and}`)

    if (!names?.length) {
      return null
    }

    return replace(this.translations.reminder.birthdayGroupText, {
      names: joinedNames
    })
  }

  /**
   * Gets the action text based on the event
   * @returns {string} The action text
   */
  get actionText(): string {
    switch (this.event) {
      case NotificationEvents.TaskAssigned:
      case NotificationEvents.TaskUnassigned:
      case NotificationEvents.TaskStatusChanged:
      case NotificationEvents.TaskCommented:
      case NotificationEvents.TaskMentioned:
      case NotificationEvents.TaskDue:
      case NotificationEvents.TaskCommentReacted:
        return this.translations.task.actionText
      case NotificationEvents.ProjectAssigned:
      case NotificationEvents.ProjectAutoConnected:
      case NotificationEvents.ProjectStatusChanged:
      case NotificationEvents.ProjectCommented:
      case NotificationEvents.ProjectMentioned:
      case NotificationEvents.ProjectDue:
      case NotificationEvents.ProjectDueWeekend:
      case NotificationEvents.ProjectCommentReacted:
        return this.translations.project.actionText
      case NotificationEvents.WeeklyTimeEntriesReport:
      case NotificationEvents.MonthlyTimeEntriesReport:
        return this.translations.timeTracking.actionText
      case NotificationEvents.ExportAvailable:
        return this.translations.exportAvailable.actionText
      case NotificationEvents.ExportFailed:
        return null
      case NotificationEvents.Birthday:
        return this.translations.user.actionText
      case NotificationEvents.DocumentCommented:
      case NotificationEvents.DocumentCommentReacted:
      case NotificationEvents.DocumentCommentMentioned:
      case NotificationEvents.DocumentMentioned:
      case NotificationEvents.DocumentContributorAdded:
        return this.translations.document.actionText
      case NotificationEvents.DocumentSpaceContributorAdded:
        return this.translations.documentSpace.actionText
      case NotificationEvents.FilterContributorAdded:
        return this.translations.filter.actionText
      case NotificationEvents.AutomationDeactivated:
        return this.translations.automationDeactivated.actionText
      case NotificationEvents.MeetingRecordingBlocked:
        return this.translations.meetingRecordingBlocked.actionText
      case NotificationEvents.AgentMeetingSummarized:
        return this.translations.agentMeetingSummarized.actionText
      case NotificationEvents.AgentThreadShared:
      case NotificationEvents.AgentThreadAwaitingAction:
        return this.translations.agentThreadAwaitingAction.actionText
      case NotificationEvents.AgentShared:
        return this.translations.agentShared.actionText
      case NotificationEvents.AiCreditsLow:
        return this.translations.aiCredits.viewPlanActionText
      case NotificationEvents.AiCreditsUsedUp:
      case NotificationEvents.AiAgentRunBlockedAdmin:
        return this.translations.aiCredits.upgradePlanActionText
      case NotificationEvents.AiAgentRunBlocked:
        return this.translations.aiCredits.viewCreditsActionText
      case NotificationEvents.AiPlanUpgraded:
        return this.translations.aiCredits.openAiActionText
      default:
        return null
    }
  }

  /**
   * Gets the navigation command of the notification based on the event
   * @returns {NotificationNavigationCommand[]}
   */
  get navigation(): NotificationNavigationCommand[] {
    switch (this.event) {
      case NotificationEvents.TaskAssigned:
      case NotificationEvents.TaskUnassigned:
      case NotificationEvents.TaskStatusChanged:
      case NotificationEvents.TaskCommented:
      case NotificationEvents.TaskCommentReacted:
      case NotificationEvents.TaskMentioned:
      case NotificationEvents.TaskDue:
      case NotificationEvents.TaskPredecessorFinished:
        return ['/tasks', this.taskId, 'details']
      case NotificationEvents.ProjectAssigned:
      case NotificationEvents.ProjectAutoConnected:
      case NotificationEvents.ProjectStatusChanged:
      case NotificationEvents.ProjectCommented:
      case NotificationEvents.ProjectCommentReacted:
      case NotificationEvents.ProjectMentioned:
      case NotificationEvents.ProjectDue:
      case NotificationEvents.ProjectDueWeekend:
        return ['/projects', this.projectId, 'details']
      case NotificationEvents.WeeklyTimeEntriesReport:
      case NotificationEvents.MonthlyTimeEntriesReport:
        return ['/time-tracking/my-day']
      case NotificationEvents.Birthday:
        return ['/users', this.userInNotificationId, 'details']
      case NotificationEvents.DocumentCommented:
      case NotificationEvents.DocumentCommentReacted:
      case NotificationEvents.DocumentCommentMentioned:
      case NotificationEvents.DocumentMentioned:
      case NotificationEvents.DocumentContributorAdded:
        return ['/docs', this.documentId]
      case NotificationEvents.DocumentSpaceContributorAdded:
        return ['/docs', 'spaces', this.documentSpaceId]
      case NotificationEvents.FilterContributorAdded:
        return ['/tasks', 'filters', this.filterId]
      case NotificationEvents.AutomationDeactivated:
        if (this.projectId) {
          return ['/projects', this.projectId, 'settings']
        }

        return ['/settings']
      case NotificationEvents.MeetingRecordingBlocked:
        return ['/meetings']
      case NotificationEvents.AgentMeetingSummarized:
        return this.documentId ? ['/docs', this.documentId] : ['/ai', 'thread', this.agentThreadId]
      case NotificationEvents.AgentThreadShared:
      case NotificationEvents.AgentThreadAwaitingAction:
        return ['/ai', 'thread', this.agentThreadId]
      case NotificationEvents.AgentShared:
        return ['/ai', { outlets: { agentModal: ['agent', this.agentId] } }]
      case NotificationEvents.AiCreditsLow:
      case NotificationEvents.AiCreditsUsedUp:
      case NotificationEvents.AiAgentRunBlockedAdmin:
        return ['/users', 'new-list']
      case NotificationEvents.AiAgentRunBlocked:
        return ['/my', 'profile', 'mysettings']
      case NotificationEvents.AiPlanUpgraded:
        return ['/ai']
      default:
        return null
    }
  }

  /**
   * Gets the navigation query params of the notification based on the event.
   * @returns {Record<string, string> | undefined} The query params, or undefined when the target needs none.
   */
  get navigationQueryParams(): Record<string, string> | undefined {
    switch (this.event) {
      case NotificationEvents.AutomationDeactivated:
        return this.projectId && this.automationId
          ? { automationId: this.automationId, notificationId: this.id }
          : undefined
      case NotificationEvents.TaskCommented:
      case NotificationEvents.TaskCommentReacted:
      case NotificationEvents.TaskMentioned:
      case NotificationEvents.ProjectCommented:
      case NotificationEvents.ProjectCommentReacted:
      case NotificationEvents.ProjectMentioned:
      case NotificationEvents.DocumentCommented:
      case NotificationEvents.DocumentCommentReacted:
      case NotificationEvents.DocumentCommentMentioned:
      case NotificationEvents.CommentMentioned:
      case NotificationEvents.CommentReacted: {
        const commentId = this.commentId || this.comment?.id

        return commentId ? { comment: commentId } : undefined
      }
      case NotificationEvents.AiAgentRunBlocked:
        return { [MY_PROFILE_SECTION_QUERY_PARAM]: MY_PROFILE_AI_SECTION }
      default:
        return undefined
    }
  }

  /**
   * Gets the mobile app destination of the notification. Entity events reuse `entityType`, so
   * task, project and document events stay in one list; only mobile-specific screens are listed here.
   * @returns {MobileNotificationTarget} The destination, or Inbox when mobile has no matching screen.
   */
  get mobileTarget(): MobileNotificationTarget {
    const commentId = this.navigationQueryParams?.comment

    switch (this.event) {
      case NotificationEvents.TaskPredecessorFinished:
        return this.taskId ? { kind: 'task', id: this.taskId } : { kind: 'taskList' }
      case NotificationEvents.DocumentSpaceContributorAdded:
        return this.documentSpaceId ? { kind: 'documentSpace', id: this.documentSpaceId } : { kind: 'inbox' }
      case NotificationEvents.AgentMeetingSummarized:
        if (this.documentId) {
          return { kind: 'document', id: this.documentId }
        }

        return this.agentThreadId ? { kind: 'thread', id: this.agentThreadId } : { kind: 'inbox' }
      case NotificationEvents.AgentThreadShared:
      case NotificationEvents.AgentThreadAwaitingAction:
        return this.agentThreadId ? { kind: 'thread', id: this.agentThreadId } : { kind: 'inbox' }
      case NotificationEvents.AiPlanUpgraded:
        return { kind: 'assistant' }
      case NotificationEvents.WeeklyTimeEntriesReport:
      case NotificationEvents.MonthlyTimeEntriesReport:
      case NotificationEvents.TimeTrackingReminder:
        return { kind: 'timer' }
      case NotificationEvents.ExportAvailable:
        return this.file ? { kind: 'export', file: this.file } : { kind: 'inbox' }
      case NotificationEvents.AutomationDeactivated:
        // Mobile has no automation settings.
        return { kind: 'inbox' }
    }

    // Generic comment events may carry only the loaded entity.
    const taskId = this.taskId || this.task?.id
    const projectId = this.projectId || this.project?.id
    const documentId = this.documentId || this.document?.id

    switch (this.entityType) {
      case 'tasks':
        return taskId ? { kind: 'task', id: taskId, commentId } : { kind: 'taskList' }
      case 'projects':
        return projectId ? { kind: 'project', id: projectId, commentId } : { kind: 'projectList' }
      case 'documents':
        return documentId ? { kind: 'document', id: documentId, commentId } : { kind: 'inbox' }
      default:
        return { kind: 'inbox' }
    }
  }

  /**
   * Gets the entity type of the notification based on the event
   * @returns {ValidEntityTypes}
   */
  get entityType(): ValidEntityTypes {
    switch (this.event) {
      case NotificationEvents.TaskAssigned:
      case NotificationEvents.TaskUnassigned:
      case NotificationEvents.TaskStatusChanged:
      case NotificationEvents.TaskCommented:
      case NotificationEvents.TaskCommentReacted:
      case NotificationEvents.TaskMentioned:
      case NotificationEvents.TaskDue:
        return 'tasks'
      case NotificationEvents.ProjectAssigned:
      case NotificationEvents.ProjectAutoConnected:
      case NotificationEvents.ProjectStatusChanged:
      case NotificationEvents.ProjectCommented:
      case NotificationEvents.ProjectCommentReacted:
      case NotificationEvents.ProjectMentioned:
      case NotificationEvents.ProjectDue:
      case NotificationEvents.ProjectDueWeekend:
        return 'projects'
      case NotificationEvents.WeeklyTimeEntriesReport:
      case NotificationEvents.MonthlyTimeEntriesReport:
        return 'timetrackings'
      case NotificationEvents.DocumentCommented:
      case NotificationEvents.DocumentMentioned:
      case NotificationEvents.DocumentCommentReacted:
      case NotificationEvents.DocumentCommentMentioned:
      case NotificationEvents.DocumentContributorAdded:
      case NotificationEvents.DocumentSpaceContributorAdded:
        return 'documents'
      case NotificationEvents.FilterContributorAdded:
        return 'filters'
      case NotificationEvents.AgentThreadShared:
      case NotificationEvents.AgentMeetingSummarized:
      case NotificationEvents.AgentThreadAwaitingAction:
      case NotificationEvents.AgentShared:
        return 'agents'
      case NotificationEvents.AutomationDeactivated:
        return this.projectId ? 'projects' : null
      case NotificationEvents.CommentMentioned:
      case NotificationEvents.CommentReacted:
        return this.resolveCommentEntityType()
      default:
        return null
    }
  }

  /**
   * Resolves entity type for generic comment events based on available entity references.
   * @returns {ValidEntityTypes} Resolved entity type.
   */
  private resolveCommentEntityType(): ValidEntityTypes {
    if (this.taskId || this.task) {
      return 'tasks'
    }

    if (this.documentId || this.document) {
      return 'documents'
    }

    if (this.projectId || this.project) {
      return 'projects'
    }

    return null
  }

  /**
   * Gets the entity name of the notification based on the event
   * @returns {string}
   */
  get entityName(): string {
    switch (this.event) {
      case NotificationEvents.TaskAssigned:
      case NotificationEvents.TaskUnassigned:
      case NotificationEvents.TaskStatusChanged:
      case NotificationEvents.TaskCommented:
      case NotificationEvents.TaskCommentReacted:
      case NotificationEvents.TaskMentioned:
      case NotificationEvents.TaskDue:
        return this.task?.name
      case NotificationEvents.ProjectAssigned:
      case NotificationEvents.ProjectAutoConnected:
      case NotificationEvents.ProjectStatusChanged:
      case NotificationEvents.ProjectCommented:
      case NotificationEvents.ProjectCommentReacted:
      case NotificationEvents.ProjectMentioned:
      case NotificationEvents.ProjectDue:
      case NotificationEvents.ProjectDueWeekend:
        return this.project?.name
      case NotificationEvents.DocumentCommented:
      case NotificationEvents.DocumentMentioned:
      case NotificationEvents.DocumentCommentReacted:
      case NotificationEvents.DocumentCommentMentioned:
      case NotificationEvents.DocumentContributorAdded:
        return this.document?.name
      case NotificationEvents.FilterContributorAdded:
        return this.filter?.name
      case NotificationEvents.DocumentSpaceContributorAdded:
        return this.documentSpace?.name
      case NotificationEvents.AutomationDeactivated:
        return this.project?.name
      case NotificationEvents.AgentThreadShared:
      case NotificationEvents.AgentMeetingSummarized:
      case NotificationEvents.AgentThreadAwaitingAction:
        return this.agentThreadTitle
      case NotificationEvents.AgentShared:
        return this.agentName
      case NotificationEvents.WeeklyTimeEntriesReport:
      case NotificationEvents.MonthlyTimeEntriesReport:
      default:
        return null
    }
  }

  /**
   * Gets the name for the group notification based on the parent entity (if any)
   * @returns {GroupNameByParentEntity}
   */
  get groupNameByParentEntity(): GroupNameByParentEntity {
    // Project tasks, project documents, and direct project comments
    if (this.task?.projectId || this.document?.projectId || this.project?.id) {
      return 'project'
    }

    // Space documents
    if (this.document?.documentSpaceId) {
      return 'documentSpace'
    }

    // Private task
    if (this.task && (!this.task.projectId || !this.project) && this.task.baseType === 'private') {
      return 'privateTask'
    }

    // Private document and shared document
    if (this.document?.isPrivate) {
      return 'privateDocument'
    }

    return undefined
  }

  /**
   * Gets the entity Id based on the entity type
   * @returns {string}
   */
  get entityId(): string {
    switch (this.entityType) {
      case 'tasks':
        return this.taskId
      case 'projects':
        return this.projectId
      case 'timetrackings':
        return this.timeTrackingId
      case 'documents':
        return this.documentId
      case 'documentspaces':
        return this.documentSpaceId
      case 'filters':
        return this.filterId
      case 'agents':
        return this.event === NotificationEvents.AgentShared ? this.agentId : this.agentThreadId
    }
  }

  /**
   * Gets the seen state of a group notification
   * @returns {boolean}
   */
  get groupSeen(): boolean {
    return this.notifications.length > 0 ? !this.notifications.some(n => !n.seen) : this.seen
  }

  /**
   * Gets a random emoji for birthday notifications based on the notification id
   * @returns {string}
   */
  private getBirthdayEmoji(): string {
    const emojis = ['🎂', '🎉', '🥂', '🎊', '🍦', '🎈', '🍰']

    return emojis[this.id.charCodeAt(0) % (emojis.length - 1)]
  }

  /**
   * Returns a short comment preview without markup.
   * @returns {string} The comment preview, or null when no comment is available.
   */
  get truncatedComment(): string {
    if (this.comment) {
      const formattedMessage = this.comment.formattedMessage?.replace(
        fileTagRegex,
        q.translations.FileTagService.deletedFile
      )

      return this.truncateString(unescape(formattedMessage), 120)
    }

    if (this.event === NotificationEvents.DocumentMentioned) {
      return this.translations.youHaveBeenMentioned
    }

    return null
  }

  /**
   * Returns the entity name for the notification preview.
   * @returns {string} The shortened name, or null when no entity name is available.
   */
  get truncatedEntityName(): string {
    if (this.entityName) {
      return this.truncateString(this.entityName, 40)
    }

    return null
  }

  /**
   * Returns the project name for the notification preview.
   * @returns {string} The shortened name, or null when no project is available.
   */
  get truncatedProjectName(): string {
    return this.project ? this.truncateString(this.project.name, 40) : null
  }

  /**
   * Whether this event belongs in the conversations list.
   * @returns {boolean} Whether the notification is a conversation.
   */
  get isConversation(): boolean {
    return CONVERSATION_TYPES.includes(this.event)
  }

  /**
   * Whether this event belongs in the event notifications list.
   * @returns {boolean} Whether the notification is an event.
   */
  get isEventNotification(): boolean {
    return EVENT_NOTIFICATION_TYPES.includes(this.event)
  }

  /**
   * Whether this event uses the default notification category.
   * @returns {boolean} Whether the notification uses the default category.
   */
  get isDefaultNotification(): boolean {
    return ![...CONVERSATION_TYPES, ...EVENT_NOTIFICATION_TYPES].includes(this.event)
  }

  /**
   * Returns the inbox group for this notification.
   * @returns {ConversationType | EventNotificationType | ProjectGroupType} The inbox group, or null for other events.
   */
  get groupType(): ConversationType | EventNotificationType | ProjectGroupType {
    if (this.isConversation) {
      return this.seen ? 'seen' : 'unseen'
    }

    switch (this.event) {
      case NotificationEvents.ProjectAssigned:
      case NotificationEvents.ProjectAutoConnected:
        return 'newProjects'
      case NotificationEvents.TaskAssigned:
        return 'newTasks'
      case NotificationEvents.TaskUnassigned:
        return 'unassignedTasks'
      case NotificationEvents.TaskPredecessorFinished:
        return 'taskPredecessorDone'
      case NotificationEvents.TaskDue:
      case NotificationEvents.ProjectDue:
      case NotificationEvents.ProjectDueWeekend:
        return 'deadlines'
      case NotificationEvents.Birthday:
        return 'birthdays'
      case NotificationEvents.ExportAvailable:
      case NotificationEvents.ExportFailed:
      case NotificationEvents.TaskScheduleSyncCompleted:
      case NotificationEvents.BillingInvoiceTransferSucceeded:
      case NotificationEvents.MeetingRecordingBlocked:
      case NotificationEvents.BillingInvoiceTransferNeedsAttention:
      case NotificationEvents.TasksImportedFinished:
      case NotificationEvents.WeeklyTimeEntriesReport:
      case NotificationEvents.MonthlyTimeEntriesReport:
        return 'reminders'
      case NotificationEvents.TaskStatusChanged: {
        const { baseType } = this.task

        return baseType === 'private' ? 'privateTaskUpdates' : 'projectUpdates'
      }
      case NotificationEvents.ProjectStatusChanged:
        return 'projectUpdates'
      case NotificationEvents.DocumentContributorAdded:
        return 'newDocuments'
      case NotificationEvents.DocumentSpaceContributorAdded:
        return 'newDocumentSpaces'
      case NotificationEvents.FilterContributorAdded:
        return 'newFilters'
      case NotificationEvents.AgentShared:
        return 'newSharedAgents'
      case NotificationEvents.AgentThreadShared:
        return 'newSharedThreads'
      case NotificationEvents.AutomationDeactivated:
      case NotificationEvents.AgentMeetingSummarized:
      case NotificationEvents.AgentThreadAwaitingAction:
      case NotificationEvents.AiCreditsLow:
      case NotificationEvents.AiCreditsUsedUp:
      case NotificationEvents.AiAgentRunBlocked:
      case NotificationEvents.AiAgentRunBlockedAdmin:
      case NotificationEvents.AiPlanUpgraded:
        return 'reminders'
      default:
        return null
    }
  }

  /**
   * Removes HTML before limiting the visible text length.
   * @param {string} string
   * @param {number} length
   * @returns {string}
   */
  truncateString(string: string, length: number): string {
    if (string) {
      const text = stripHTML(string)

      return text.length > length ? text.slice(0, length) + '...' : text
    }

    return ''
  }

  /**
   * Describes an automation that stopped after a failure.
   * @returns {string} The notification description.
   */
  private getAutomationDeactivatedDescription(): string {
    return this.translations.automationDeactivated.description
  }

  /**
   * Explains the automation failure when a reason is available.
   * @returns {string} The failure details, or null when no reason is available.
   */
  private getAutomationDeactivatedDetail(): string {
    if (this.automationFailureCode && this.automationFailureDescription) {
      return replace(this.translations.automationDeactivated.detailWithReasonAndFailureDescription, {
        failureCode: this.automationFailureCode,
        failureDescription: this.automationFailureDescription
      })
    }

    if (this.automationFailureCode) {
      return replace(this.translations.automationDeactivated.detailWithReason, {
        failureCode: this.automationFailureCode
      })
    }

    if (this.automationFailureDescription) {
      return replace(this.translations.automationDeactivated.detailWithFailureDescription, {
        failureDescription: this.automationFailureDescription
      })
    }

    return null
  }
}
